<template>
  <b-card class="wallet trans">
    <div class="ml-0">
      <div class="d-flex justify-content-start align-items-center mb-1">
        <div class="d-flex justify-content-between w-100 refresh-button-container">
          <div>
            <h3 class="back_button">
              <BackButton />
              <span>{{ $t("transactionHistory.Transaction-History") }}</span>
            </h3>
          </div>
          <div class="refresh-button">
            <b-button
              variant="flat-primary"
              class="btn-icon"
              @click="getAllTransactionHistory"
            >
              <feather-icon icon="RotateCwIcon" />
              <span class="align-middle ml-1">{{ $t("wallet.Refresh") }}</span>
            </b-button>
          </div>
        </div>
      </div>

      <div ref="filter" class="filter-box mobile-filter">
        <b-button
          variant="primary"
          class="filter-icon"
          @click="toggleFilter"
        >
          <feather-icon icon="FilterIcon" />
        </b-button>

        <div v-if="isFilterVisible" class="p2p-filter">
          <div class="filter-area">
            <div class="col-20-p min-180">
              <CurrencySelect
                v-model="coin"
                class="min-drop"
                :placeholder="$t('transactionHistory.Coin')"
                :name="$t('transactionHistory.Coin')"
                autocomplete="off"
                :clearable="true"
                :options="coins"
              />
            </div>

            <div class="col-20-p min-150">
              <v-select
                v-model="type"
                :placeholder="$t('transactionHistory.Type')"
                :options="refData.txType"
              />
            </div>

            <div class="col-20-p min-150">
              <v-select
                v-model="status"
                :placeholder="$t('transactionHistory.Status')"
                :options="refData.txStatus"
              />
            </div>

            <div class="col-20-p col-20-p-date">
              <DatePicker
                v-model="selectedDates"
                :max-date="new Date()"
                :placeholder="$t('transactionHistory.searchDate')"
                :single-date-picker="false"
              />
            </div>

            <div class="col-20-p small-search">
              <b-input-group class="input-group-merge">
                <b-input-group-prepend is-text>
                  <feather-icon icon="SearchIcon" />
                </b-input-group-prepend>
                <b-form-input
                  v-model="searchText"
                  :placeholder="$t('transactionHistory.Search')"
                />
              </b-input-group>
            </div>

            <div>
              <b-button
                variant="flat-primary"
                class="btn-icon"
                @click="resetAll"
              >
                <span class="align-middle">{{ $t("transactionHistory.Reset") }}</span>
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <div>
        <b-table
          :items="items"
          :fields="fields"
          show-empty
          responsive
        >
          <template #cell(toAddress)="data">
            <b-link v-if="data.value" class="no-wrap">
              <span
                v-b-tooltip.hover.v-primary
                class="mr-1"
                :title="data.value"
              >
                {{ data.item.toAddressStr }}
              </span>
              <copy
                v-b-tooltip.hover.v-primary
                :title="$t('wallet.Copy')"
                @click="copy(data.value)"
              />
            </b-link>
          </template>

          <template #cell(txId)="data">
            <b-link v-if="data.value" class="no-wrap">
              <span
                v-b-tooltip.hover.v-primary
                class="mr-1"
                :title="data.value"
              >
                {{ data.item.txIdStr }}
              </span>
              <copy
                v-b-tooltip.hover.v-primary
                :title="$t('wallet.Copy')"
                @click="copy(data.value)"
              />
            </b-link>
          </template>
        </b-table>

        <b-row>
          <b-col cols="12" sm="12" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination
              v-if="isPaginationVisible"
              :key="page"
              v-model="page"
              :total-rows="totalRows"
              :per-page="size"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
              class="mb-0 mt-1 mt-sm-0"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BButton,
  BTable,
  BRow,
  BCol,
  BPagination,
  BInputGroup,
  BInputGroupPrepend,
  BFormInput,
  BLink,
  VBTooltip,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import { mapState } from 'vuex';
import copy from '@/assets/rashi-coin/copy.svg';
import APIService from '@/libs/api/api';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import constants from '@/utils/constants';
import copyToClipboard from '@/utils/clipboard';
import CurrencySelect from '@/layouts/components/CurrencySelect.vue';
import DatePicker from '@/layouts/components/DatePicker.vue';
import BackButton from '@/layouts/components/BackButton.vue';

export default {
  name: 'TransactionHistory',
  components: {
    BCard,
    BButton,
    BTable,
    BRow,
    BCol,
    BPagination,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    BLink,
    vSelect,
    copy,
    CurrencySelect,
    DatePicker,
    BackButton,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      fields: [
        {
          key: 'createdTime',
          label: this.$t('Order.CreateTime'),
          tdClass: 'n-wrap',
          thClass: 'n-wrap',
        },
        {
          key: 'txType',
          label: this.$t('transactionHistory.Type'),
        },
        {
          key: 'coin',
          label: this.$t('transactionHistory.Coin'),
        },
        {
          key: 'totalAmount',
          label: this.$t('transactionHistory.Amount'),
        },
        {
          key: 'toAddress',
          label: this.$t('transactionHistory.Destination'),
        },
        {
          key: 'txId',
          label: this.$t('transactionHistory.TxId'),
        },
        {
          key: 'txStatus',
          label: this.$t('transactionHistory.Status'),
        },
      ],
      items: [],
      selectedDates: '',
      status: '',
      searchText: '',
      coin: '',
      debounceTimeout: null,
      type: '',
      isFilterVisible: false,
      startDate: '',
      endDate: '',
      firstTime: true,
      page: 1,
      size: 10,
      lang: this.$route.params.lang,
      isPaginationVisible: false,
      totalRows: 0,
    };
  },
  computed: {
    ...mapState('user', ['refData', 'user', 'coins']),
  },
  watch: {
    size() {
      if (!this.size) {
        this.size = 10;
      }
      this.getAllTransactionHistory();
    },
    page() {
      constants.setPagination.apply(this, [`${this.page}`]);
      this.getAllTransactionHistory();
    },
    selectedDates(newVal) {
      constants.setQueryParams.apply(this, [{ selectedDates: newVal }]);
      this.search();
    },
    type(newVal) {
      constants.setQueryParams.apply(this, [{ type: newVal }]);
      this.search();
    },
    coin(newVal, oldVal) {
      constants.setQueryParams.apply(this, [{ coin: newVal }]);
      if (oldVal !== newVal) this.search();
    },
    searchText(newVal, oldVal) {
      constants.setQueryParams.apply(this, [{ searchText: newVal }]);
      if (oldVal !== newVal) this.search();
    },
    status(newVal, oldVal) {
      constants.setQueryParams.apply(this, [{ status: newVal }]);
      if (oldVal !== newVal) this.search();
    },
  },
  mounted() {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.isFilterVisible = entry.contentRect.width > 667;
      }
    });
    resizeObserver.observe(document.body);
    document.addEventListener('click', this.handleClickOutside);

    let flag = 0;
    if (this.$route?.query?.page) {
      this.page = this.$route.query.page;
      flag++;
    }
    if (this.$route?.query?.searchText && this.searchText !== this.$route.query.searchText) {
      this.searchText = this.$route.query.searchText;
      flag++;
    }
    if (this.$route?.query?.type && this.type !== this.$route.query.type) {
      this.type = this.$route.query.type;
      flag++;
    }
    if (this.$route?.query?.coin && this.coin !== this.$route.query.coin) {
      this.coin = this.$route.query.coin;
      flag++;
    }
    if (this.$route?.query?.status && this.status !== this.$route.query.status) {
      this.status = this.$route.query.status;
      flag++;
    }
    if (this.$route?.query?.selectedDates && this.selectedDates !== this.$route.query.selectedDates) {
      this.selectedDates = this.$route.query.selectedDates;
      flag++;
    }
    if (!flag) {
      this.getAllTransactionHistory();
    }
    setTimeout(() => {
      this.firstTime = false;
    }, 5000);
  },
  destroyed() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    handleClickOutside(event) {
      if (window.innerWidth < 667) {
        if (this.$refs.filter && !this.$refs.filter.contains(event.target)) {
          this.isFilterVisible = false;
        }
      }
    },
    async copy(text) {
      await copyToClipboard.apply(this, [`${text}`]);
    },
    toggleFilter() {
      this.isFilterVisible = !this.isFilterVisible;
    },
    resetAll() {
      this.selectedDates = '';
      this.status = '';
      this.searchText = '';
      this.coin = '';
      this.type = '';
      this.startDate = '';
      this.endDate = '';
      this.firstTime = true;
      setTimeout(() => {
        this.firstTime = false;
      }, 5000);
    },
    customSort(a, b, key, order) {
      if (key !== this.field && order !== this.order) {
        this.order = order;
        this.field = key;
      }
    },
    search() {
      let startDate = null;
      let endDate = null;
      if (this.selectedDates && this.selectedDates.length) {
        const [startDateString, endDateString] = this.selectedDates.split(' to ');
        startDate = new Date(startDateString);
        endDate = endDateString ? new Date(endDateString) : new Date();
        startDate.setHours(23, 59, 59);
        endDate.setHours(23, 59, 59);
        this.startDate = startDate.toISOString().split('T')[0];
        this.endDate = endDate.toISOString().split('T')[0];
      }
      const debounceTime = 500;
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }
      this.debounceTimeout = setTimeout(() => {
        if (this.firstTime || this.page === 1) {
          this.getAllTransactionHistory();
        }
        if (!this.firstTime) {
          this.page = 1;
        }
      }, debounceTime);
    },
    async getAllTransactionHistory() {
      try {
        const res = await new APIService().api(
          {
            method: 'GET',
            url: 'asset/tx/search',
          },
          {
            searchText: this.searchText || undefined,
            txType: this.type || undefined,
            coin: this.coin || undefined,
            txStatus: this.status || undefined,
            startDate: this.startDate || undefined,
            endDate: this.endDate || undefined,
            page: this.page,
            size: this.size,
          },
          {
            isLoading: true,
          }
        );

        if (res && (res.cryptoTxList || Array.isArray(res))) {
          const list = res.cryptoTxList || res;
          this.items = list.map((item) => {
            const rawTxId = item.txId || item.txID || item.id;
            const rawAddress = item.toAddress || item.address;
            const rawDate = item.createdTime || item.createdAt;
            return {
              ...item,
              txId: rawTxId,
              toAddress: rawAddress,
              createdTime: constants.dateFormat(rawDate),
              txIdStr: constants.showOnlyLastFiveChar(rawTxId),
              toAddressStr: constants.showOnlyLastFiveChar(rawAddress),
            };
          });
          this.totalRows = res.count !== undefined ? res.count : this.items.length;
          this.isPaginationVisible = Boolean(this.totalRows && this.totalRows > this.size);
        } else if (res && res.error && res.error.message) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.error.message,
              icon: 'EditIcon',
              variant: 'danger',
            },
          });
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.message || error || 'Error fetching transactions',
            icon: 'EditIcon',
            variant: 'danger',
          },
        });
      }
    },
  },
};
</script>

<style scoped>
[dir] .filter-box {
  margin-top: 0px;
  margin-bottom: 10px;
}
.p2p-filter {
  display: flex;
  justify-content: space-between;
  width: 100%;
}
[dir] .p2p-filter {
  margin-bottom: 10px;
}
.p2p-filter .filter-area {
  display: flex;
  align-items: center;
}
[dir] .p2p-filter .filter-area .vs--single .vs__dropdown-toggle {
  padding: 2px;
}
[dir="ltr"] .p2p-filter .filter-area .vs__actions {
  padding: 0px 6px 0 3px;
}
[dir="rtl"] .p2p-filter .filter-area .vs__actions {
  padding: 0px 3px 0 6px;
}
.p2p-filter .filter-area .col-20-p {
  min-width: 210px;
}
[dir] .p2p-filter .filter-area .col-20-p {
  margin-bottom: 6px;
}
[dir="ltr"] .p2p-filter .filter-area .col-20-p {
  padding-right: 15px;
}
[dir="rtl"] .p2p-filter .filter-area .col-20-p {
  padding-left: 15px;
}
.p2p-filter .filter-area .col-20-p .min-drop.v-select {
  min-width: 200px;
}
.p2p-filter .filter-area .col-20-p.min-180 {
  min-width: 235px;
}
.p2p-filter .filter-area .col-20-p.col-20-p-date {
  min-width: 240px;
  display: flex;
}
.p2p-filter .filter-area .col-20-p.small-search {
  min-width: 240px;
}
[dir="ltr"] .p2p-filter .refresh-btn {
  text-align: right;
}
[dir="rtl"] .p2p-filter .refresh-btn {
  text-align: left;
}
.content1 {
  color: #8dc540;
  font-size: 34px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 2.38px;
}
.h4 {
  color: var(--text-black-color);
  font-size: 21px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 1.47px;
}
[dir] .h4 {
  margin: 16px 0px 16px 0px;
}
.amt {
  color: var(--text-black-color);
  font-size: 19px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 1.33px;
}
.Search {
  color: #6e6b7b;
  font-size: 16px;
  font-weight: 400;
}
.text-lable {
  color: var(--text-black-color);
  font-size: 13.234px;
  font-weight: 600;
  line-height: 16px;
}
@media (max-width: 991px) {
  [dir] .p2p-filter .col-20-p-date {
    margin-bottom: 5px;
  }
  [dir] .p2p-filter .vue-daterange-picker.form-control.date-pickr {
    margin-bottom: 0px;
  }
  [dir] .trans-filter {
    margin-top: -25px;
    margin-bottom: 15px;
  }
}
</style>
